import { useEffect, useRef, useState } from "react";
import { Form, Col, Row, Button } from "antd";
import usePost from "../../../global/hooks/usePost";
import useGet from "../../../global/hooks/useGet";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import { useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import { CloseOutlined } from "@ant-design/icons";
import { RiQrCodeFill } from "react-icons/ri";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { DuplicatePlateModal } from "./DuplicatePlateModal";

const PLATE_PRODUCTION_BASE = `${process.env.REACT_APP_API_URL}/api/plates`;

const randomPlateCode = () =>
  String(Math.floor(100000000 + Math.random() * 900000000));

export const PlateProductionAddbyCount = ({
  isModalForm,
  onCancel,
  platesRefetch,
}) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const [modal, setModal] = useState(null);
  const autoFilledRef = useRef(null);

  const { data: lastCodeData, error: lastCodeError } = useGet(
    `${process.env.REACT_APP_API_URL}/api/plates/?sort_by=PlateNo&order=desc&limit=1`,
  );

  const postUrl = `${PLATE_PRODUCTION_BASE}/bulk-create-by-count/`;

  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  useEffect(() => {
    if (!lastCodeData && !lastCodeError) return;
    const current = form.getFieldValue("PlateCode");
    if (
      current != null &&
      String(current).trim() !== "" &&
      String(current) !== String(autoFilledRef.current ?? "")
    ) {
      return;
    }
    const rows = Array.isArray(lastCodeData?.data) ? lastCodeData.data : [];
    const last = rows[0]?.PlateCode;
    const next =
      last != null && String(last).trim() !== "" && Number.isFinite(Number(last))
        ? String(Number(last) + 1)
        : randomPlateCode();
    autoFilledRef.current = next;
    form.setFieldsValue({ PlateCode: next });
  }, [form, lastCodeData, lastCodeError]);

  const onFinish = async (values) => {
    const modValues = {
      ProductionNo: Number(id),
      PlateCode: String(values.PlateCode).trim(),
      count: Number(values.count),
    };
    try {
      await postAction(modValues);
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  useEffect(() => {
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      const details = postData?.data?.details || [];
      const hasDuplicates = details.some((item) => item?.status === 400);
      if (hasDuplicates) {
        setModal(details);
        platesRefetch?.();
      } else {
        openConfirmNotification("Success");
        platesRefetch?.();
        if (isModalForm) {
          onCancel?.();
        }
      }
    }
  }, [postError, postData, postLoading]);

  return (
    <>
      <Form
        validateTrigger={"onChange"}
        form={form}
        onFinish={onFinish}
        scrollToFirstError
        layout={"vertical"}
        autoComplete={"off"}
      >
        <div
          className={`${
            isModalForm ? "" : "bg-white overflow-hidden border rounded-[4px]"
          }`}
        >
          {!isModalForm && (
            <div className="flex items-center bg-gray-200 p-2.5">
              <RiQrCodeFill className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2 ">
                Virtual QR/NFC
              </span>
            </div>
          )}

          <div className="px-2 pt-1 pb-3">
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Virtual QR/NFC Code"
                  name="PlateCode"
                  type="number"
                  required
                  integerOnly
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !/^\d+$/.test(String(value))) {
                          return Promise.reject(
                            new Error("Virtual QR/NFC Code must be a number"),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Col>
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <CustomInput
                  label="Virtual QR/NFC Count"
                  name="count"
                  type="number"
                  required
                  integerOnly
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || Number(value) < 1) {
                          return Promise.reject(
                            new Error(
                              "Virtual QR/NFC Count must be at least 1",
                            ),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                />
              </Col>
            </Row>
          </div>
        </div>
      </Form>
      {isModalForm && (
        <div className="flex justify-center space-x-2 mt-2.5">
          <Button
            type="primary"
            className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center "
            onClick={() => form.submit()}
            loading={postLoading}
            disabled={postLoading}
          >
            <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
            <span>Apply</span>
          </Button>
          <Button className="reset-button" onClick={onCancel}>
            <CloseOutlined />
            <span>Cancel</span>
          </Button>
        </div>
      )}

      <DuplicatePlateModal
        isModalOpen={modal !== null}
        setModal={setModal}
        modal={modal}
        isModalForm={isModalForm}
      />
    </>
  );
};
